import { Component } from '@theme/component';
import { ThemeEvents } from './events.js';

const YOTPO_APP_KEY = 'YgegYY61nsJhCcoEiHWN7B6hBc11zTblOMBdvKi5';
const REVIEWS_PER_PAGE = 5;
const YOTPO_MAX_PER_PAGE = 150;
const MAX_REVIEW_IMAGES = 10;
const STRIP_PHOTOS = 7;
const SWIPE_DISTANCE = 40;
const MAX_TOPICS = 8;
const MIN_TOPIC_REVIEWS = 2;

const IGNORED_TOPICS = new Set(['scooter', 'scooters', 'product', 'item', 'son', 'daughter', 'wife', 'husband']);
const SAFE_URL = /^https:\/\/[^"'<>\s]+$/;

const ICONS = {
	star: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14l-5-4.87 6.91-1.01z"/></svg>',
	search: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="6.5"/><path d="M16 16l4 4"/></svg>',
	shield: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 2l8 3v6c0 5-3.4 9.3-8 11-4.6-1.7-8-6-8-11V5z"/><path d="M8.5 12l2.4 2.4 4.6-4.8" fill="none" style="stroke: var(--color-background)" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
	verified: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 2l2.4 1.8 3-.1 1 2.8 2.4 1.8-.9 2.9.9 2.9-2.4 1.8-1 2.8-3-.1L12 22l-2.4-1.8-3 .1-1-2.8-2.4-1.8.9-2.9-.9-2.9 2.4-1.8 1-2.8 3 .1z"/><path d="M8.5 12.2l2.3 2.3 4.7-4.8" fill="none" style="stroke: var(--color-background)" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
	remove: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>',
};

class ProductLinksComponent extends Component {
	#selectedVariantId = null;
	#variantSpecs = null;
	#strings = null;
	/** Every customer photo, newest review first, as { image, review } */
	#photos = [];
	#photoIndexOf = new Map();
	#photoIndex = 0;
	#reviewFiles = [];
	#listScroll = 0;
	#opener = null;

	connectedCallback() {
		super.connectedCallback();

		const variantSpecsJson = this.getAttribute('data-variant-specs');
		if (variantSpecsJson) {
			try {
				this.#variantSpecs = JSON.parse(variantSpecsJson);
			} catch (e) {
				console.error('Failed to parse variant specs JSON:', e);
			}
			this.#selectedVariantId = this.getAttribute('data-selected-variant-id');
			this.#renderVariantSpecs();
			this.#bindVariantEvents();
		} else {
			this.#renderSpecsTable();
		}

		const reviews = /** @type {HTMLElement | null} */ (this.querySelector('[data-reviews]'));
		if (!reviews) return;

		this.#strings = JSON.parse(/** @type {HTMLElement} */ (reviews.querySelector('[data-reviews-content]')).dataset.strings);
		const drawer = this.querySelector('#product-links-reviews-drawer');
		drawer.addEventListener('dialog:open', () => this.#loadReviews(), { once: true });
		drawer.addEventListener('dialog:close', () => this.#showView('list', false));
		reviews.addEventListener('click', (e) => this.#onReviewsClick(/** @type {HTMLElement} */ (e.target)));
		reviews.addEventListener('keydown', (e) => {
			if (reviews.dataset.view !== 'photo' || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
			e.preventDefault();
			this.#openPhoto(this.#photoIndex + (e.key === 'ArrowLeft' ? -1 : 1));
		});
		this.#bindSwipe(reviews.querySelector('[data-photo-stage]'));

		const form = reviews.querySelector('[data-review-form]');
		form.addEventListener('submit', (e) => this.#submitReview(e));
		form.addEventListener('change', (e) => {
			const input = /** @type {HTMLInputElement} */ (e.target);
			if (input.name === 'review_score') {
				form.querySelector('[data-score-word]').textContent = this.#strings.ratingWords[Number(input.value) - 1];
			} else if (input.matches('[data-review-images]')) {
				this.#addReviewImages(input);
			}
		});
	}

	#onReviewsClick(target) {
		const control = /** @type {HTMLElement | null} */ (
			target.closest('[data-reviews-write], [data-reviews-back], [data-photo-index], [data-photo-step], [data-remove-photo]')
		);
		if (!control) return;
		const { photoIndex, photoStep, removePhoto } = control.dataset;

		if (control.hasAttribute('data-reviews-write')) {
			if (this.querySelector('[data-review-form]').hidden) this.#resetReviewForm();
			this.#showView('write');
		} else if (control.hasAttribute('data-reviews-back')) {
			this.#showView('list');
		} else if (photoIndex !== undefined) {
			this.#openPhoto(Number(photoIndex));
		} else if (photoStep !== undefined) {
			this.#openPhoto(this.#photoIndex + Number(photoStep));
		} else {
			this.#removeReviewImage(Number(removePhoto));
		}
	}

	/** Swaps the drawer between its list, photo and write views, keeping the list's scroll position. */
	#showView(view, moveFocus = true) {
		const root = /** @type {HTMLElement} */ (this.querySelector('[data-reviews]'));
		if (root.dataset.view === view) return;

		const list = root.querySelector('[data-reviews-pane="list"] .product-links-reviews__scroll');
		if (root.dataset.view === 'list') {
			this.#listScroll = list.scrollTop;
			this.#opener = document.activeElement;
		}
		root.dataset.view = view;
		if (moveFocus) root.dataset.switched = '';
		else delete root.dataset.switched;

		for (const pane of root.querySelectorAll('[data-reviews-pane]')) {
			/** @type {HTMLElement} */ (pane).hidden = /** @type {HTMLElement} */ (pane).dataset.reviewsPane !== view;
		}
		const back = /** @type {HTMLElement} */ (root.querySelector('[data-reviews-back]'));
		back.hidden = view === 'list';
		if (view !== 'photo') {
			root.querySelector('[data-reviews-title]').textContent = view === 'write' ? this.#strings.writeReview : this.#strings.reviews;
		}

		if (view === 'list') {
			list.scrollTop = this.#listScroll;
			if (moveFocus && this.#opener instanceof HTMLElement && this.#opener.isConnected) this.#opener.focus({ preventScroll: true });
		} else if (moveFocus) {
			back.focus();
		}
	}

	#openPhoto(index) {
		const count = this.#photos.length;
		if (!count) return;
		this.#photoIndex = (index + count) % count;
		const { image, review } = this.#photos[this.#photoIndex];
		const root = this.querySelector('[data-reviews]');

		this.#showView('photo');
		/** @type {HTMLImageElement} */ (root.querySelector('[data-photo-image]')).src = image.original_url;
		const counter = this.#strings.photoCount.replace('[index]', this.#photoIndex + 1).replace('[count]', count);
		root.querySelector('[data-reviews-title]').innerHTML = `${this.#escapeHtml(this.#strings.customerPhotos)}
			<span class="product-links-reviews__counter">${this.#escapeHtml(counter)}</span>`;
		for (const step of root.querySelectorAll('[data-photo-step]')) /** @type {HTMLElement} */ (step).hidden = count < 2;
		for (const thumb of root.querySelectorAll('[data-photo-film] [data-photo-index]')) {
			const current = Number(/** @type {HTMLElement} */ (thumb).dataset.photoIndex) === this.#photoIndex;
			thumb.setAttribute('aria-current', String(current));
			if (current) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
		}

		const name = this.#decode(review.user?.display_name || '');
		const byline = review.verified_buyer && name ? `${name} · ${this.#strings.verified}` : name;
		root.querySelector('[data-photo-review]').innerHTML = `${this.#stars(review.score)}
			${review.title ? `<h3 class="product-links-reviews__title">${this.#escapeYotpo(review.title)}</h3>` : ''}
			<p class="product-links-reviews__body">${this.#escapeYotpo(review.content)}</p>
			<p class="product-links-reviews__date">${this.#escapeHtml(byline)}</p>`;
	}

	#bindSwipe(stage) {
		let startX = null;
		stage.addEventListener('pointerdown', (e) => {
			if (e.pointerType !== 'mouse') startX = e.clientX;
		});
		stage.addEventListener('pointerup', (e) => {
			if (startX === null) return;
			const distance = e.clientX - startX;
			startX = null;
			if (Math.abs(distance) > SWIPE_DISTANCE) this.#openPhoto(this.#photoIndex + (distance < 0 ? 1 : -1));
		});
		stage.addEventListener('pointercancel', () => (startX = null));
	}

	async #loadReviews() {
		const container = /** @type {HTMLElement} */ (this.querySelector('[data-reviews-content]'));
		const strings = this.#strings;

		// Topics are a nice-to-have, so a failed request just means no topic chips
		const topicsRequest = this.#fetchTopics(container.dataset.productId).catch((e) => {
			console.error('Failed to load review topics:', e);
			return [];
		});
		let bottomline;
		let reviews;
		try {
			// ponytail: Yotpo's review index drops or repeats reviews on small pages and differs by sort,
			// so fetch everything twice at the max page size and merge. Drop once Yotpo fixes it.
			const results = await Promise.all(['&sort=date', ''].map((sort) => this.#fetchAllReviews(container.dataset.productId, sort)));
			bottomline = results[0].bottomline;
			const byId = new Map(results.flatMap((result) => result.reviews).map((review) => [review.id, review]));
			reviews = [...byId.values()].sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id - a.id);
		} catch (e) {
			console.error('Failed to load reviews:', e);
			container.innerHTML = `<p class="product-links-reviews__empty">${this.#escapeHtml(strings.error)}</p>`;
			container.removeAttribute('aria-busy');
			return;
		}

		if (!bottomline.total_review) {
			container.innerHTML = `<p class="product-links-reviews__empty">${this.#escapeHtml(strings.noReviews)}</p>`;
			container.removeAttribute('aria-busy');
			return;
		}

		this.#photos = reviews.flatMap((review) => this.#safeImages(review).map((image) => ({ image, review })));
		this.#photoIndexOf = new Map(this.#photos.map(({ image }, index) => [image, index]));
		this.querySelector('[data-photo-film]').innerHTML = this.#photos
			.map(({ image }, index) => this.#photoButton(index, image.thumb_url))
			.join('');

		const searchText = new Map(
			reviews.map((review) => [
				review,
				this.#decode(`${review.title} ${review.content} ${review.user?.display_name ?? ''}`).toLowerCase(),
			])
		);
		const topics = this.#matchTopics(await topicsRequest, reviews, searchText);
		const distribution = bottomline.star_distribution || {};
		const ratingPills = [5, 4, 3, 2, 1]
			.filter((star) => distribution[star])
			.map(
				(star) =>
					`<button type="button" class="pills__pill" data-filter-rating="${star}" aria-pressed="false" aria-label="${this.#escapeHtml(
						strings.starsFilter.replace('[rating]', star)
					)}">${star}${ICONS.star}</button>`
			)
			.join('');

		container.innerHTML = `${this.#reviewsSummary(bottomline)}
			${this.#trustBadge(container.dataset.yotpoLogo)}
			${this.#photoStrip()}
			${this.#topicChips(topics)}
			<div class="product-links-reviews__toolbar">
				<label class="product-links-reviews__search">
					${ICONS.search}
					<span class="visually-hidden">${this.#escapeHtml(strings.search)}</span>
					<input type="search" placeholder="${this.#escapeHtml(strings.search)}">
				</label>
				<div class="product-links-reviews__pills" role="group" aria-label="${this.#escapeHtml(strings.filterReviews)}">
					<button type="button" class="pills__pill" data-filter="all" aria-pressed="true">${this.#escapeHtml(strings.all)}</button>
					${this.#photos.length ? `<button type="button" class="pills__pill" data-filter="media" aria-pressed="false">${this.#escapeHtml(strings.withMedia)}</button>` : ''}
					${ratingPills}
					<select class="pills__pill" aria-label="${this.#escapeHtml(strings.sortReviews)}">
						<option value="newest">${this.#escapeHtml(strings.sortNewest)}</option>
						<option value="highest">${this.#escapeHtml(strings.sortHighest)}</option>
						<option value="lowest">${this.#escapeHtml(strings.sortLowest)}</option>
					</select>
				</div>
			</div>
			<ul class="product-links-reviews__list" role="list"></ul>
			<button type="button" class="button button-secondary product-links-reviews__more">${this.#escapeHtml(strings.loadMore)}</button>`;

		const list = container.querySelector('.product-links-reviews__list');
		const more = /** @type {HTMLElement} */ (container.querySelector('.product-links-reviews__more'));
		const toolbar = container.querySelector('.product-links-reviews__toolbar');
		const search = /** @type {HTMLInputElement} */ (toolbar.querySelector('input'));
		const sort = /** @type {HTMLSelectElement} */ (toolbar.querySelector('select'));

		let filtered = reviews;
		let shown = 0;
		let rating = 0;
		let media = false;
		let topic = null;
		const showMore = () => {
			list.insertAdjacentHTML(
				'beforeend',
				filtered.slice(shown, shown + REVIEWS_PER_PAGE).map((review) => this.#reviewItem(review)).join('')
			);
			// Offer "Read more" only where the 3-line clamp actually cut text off
			for (const button of list.querySelectorAll('[data-read-more][hidden]')) {
				const body = /** @type {HTMLElement} */ (button.previousElementSibling);
				if (body.scrollHeight > body.clientHeight + 1) /** @type {HTMLElement} */ (button).hidden = false;
				else body.removeAttribute('data-clamp');
			}
			shown += REVIEWS_PER_PAGE;
			more.hidden = shown >= filtered.length;
		};
		const applyFilters = () => {
			const query = search.value.trim().toLowerCase();
			filtered = reviews.filter(
				(review) =>
					(!query || searchText.get(review).includes(query)) &&
					(!rating || review.score === rating) &&
					(!media || this.#safeImages(review).length) &&
					(!topic || topic.reviews.has(review))
			);
			// Array.sort is stable, so reviews with the same score stay newest first
			if (sort.value !== 'newest') filtered.sort((a, b) => (sort.value === 'highest' ? b.score - a.score : a.score - b.score));

			for (const control of container.querySelectorAll('[data-filter-rating]')) {
				control.setAttribute('aria-pressed', String(Number(/** @type {HTMLElement} */ (control).dataset.filterRating) === rating));
			}
			container.querySelector('[data-filter="media"]')?.setAttribute('aria-pressed', String(media));
			for (const control of container.querySelectorAll('[data-filter-topic]')) {
				control.setAttribute('aria-pressed', String(topics[Number(/** @type {HTMLElement} */ (control).dataset.filterTopic)] === topic));
			}
			container.querySelector('[data-filter="all"]').setAttribute('aria-pressed', String(!rating && !media && !topic));

			list.innerHTML = filtered.length
				? ''
				: `<li class="product-links-reviews__empty">${this.#escapeHtml(strings.noMatches)}</li>`;
			shown = 0;
			showMore();
		};

		list.addEventListener('click', (e) => {
			const readMore = /** @type {HTMLElement} */ (e.target).closest('[data-read-more]');
			if (!readMore) return;
			readMore.previousElementSibling.removeAttribute('data-clamp');
			readMore.remove();
		});
		container.addEventListener('click', (e) => {
			const control = /** @type {HTMLElement | null} */ (
				/** @type {HTMLElement} */ (e.target).closest('[data-filter], [data-filter-rating], [data-filter-topic]')
			);
			if (!control) return;
			if (control.dataset.filter === 'all') {
				rating = 0;
				media = false;
				topic = null;
			} else if (control.dataset.filter === 'media') {
				media = !media;
			} else if (control.dataset.filterTopic) {
				const chosen = topics[Number(control.dataset.filterTopic)];
				topic = topic === chosen ? null : chosen;
			} else {
				const star = Number(control.dataset.filterRating);
				rating = rating === star ? 0 : star;
			}
			applyFilters();
			// The summary bars and topic chips sit above the list, so bring the filtered results into view
			if (!control.closest('.product-links-reviews__toolbar')) toolbar.scrollIntoView({ block: 'start', behavior: 'smooth' });
		});
		more.addEventListener('click', showMore);
		search.addEventListener('input', applyFilters);
		sort.addEventListener('change', applyFilters);
		applyFilters();
		container.removeAttribute('aria-busy');
	}

	async #submitReview(event) {
		event.preventDefault();
		const form = /** @type {HTMLFormElement} */ (event.currentTarget);
		const status = /** @type {HTMLElement} */ (form.querySelector('.product-links-reviews__form-status'));
		const submit = /** @type {HTMLButtonElement} */ (form.querySelector('[type="submit"]'));
		const productId = /** @type {HTMLElement} */ (this.querySelector('[data-reviews-content]')).dataset.productId;
		const images = this.#reviewFiles;

		// Mirrors how Yotpo's own reviews widget posts a review (form encoded, review_source widget_v2).
		const params = new URLSearchParams(/** @type {any} */ (new FormData(form)));
		params.append('appkey', YOTPO_APP_KEY);
		params.append('review_source', 'widget_v2');
		params.append('sku', productId);
		params.append('product_title', form.dataset.productTitle);
		params.append('product_url', form.dataset.productUrl);
		params.append('product_image_url', form.dataset.productImageUrl);
		params.append('uploaded_images', JSON.stringify(images.map(({ name, type }) => ({ name, type }))));

		status.hidden = true;
		submit.disabled = true;
		let uploadToken;
		try {
			const response = await fetch('https://api-cdn.yotpo.com/v1/widget/reviews', { method: 'POST', body: params });
			if (!response.ok) throw new Error(`Yotpo responded ${response.status}`);
			uploadToken = (await response.json()).image_upload_token;
		} catch (e) {
			console.error('Failed to submit review:', e);
			status.textContent = this.#strings.submitError;
			status.hidden = false;
			submit.disabled = false;
			return;
		}

		// The review is saved at this point, so a failed photo upload is logged rather than shown as a failed review.
		if (images.length) {
			try {
				await this.#uploadReviewImages(images, uploadToken);
			} catch (e) {
				console.error('Failed to upload review photos:', e);
			}
		}
		const success = /** @type {HTMLElement} */ (this.querySelector('[data-review-success]'));
		form.hidden = true;
		success.hidden = false;
		success.closest('.product-links-reviews__scroll').scrollTop = 0;
		success.focus();
	}

	#resetReviewForm() {
		const form = /** @type {HTMLFormElement} */ (this.querySelector('[data-review-form]'));
		form.reset();
		form.hidden = false;
		/** @type {HTMLButtonElement} */ (form.querySelector('[type="submit"]')).disabled = false;
		form.querySelector('[data-score-word]').textContent = '';
		/** @type {HTMLElement} */ (this.querySelector('[data-review-success]')).hidden = true;
		this.#reviewFiles = [];
		this.#renderReviewImages();
	}

	/** Uploads photos the same way Yotpo's widget does: signed S3 upload, then attach each URL to the review. */
	async #uploadReviewImages(images, uploadToken) {
		if (!uploadToken) throw new Error('Yotpo did not return an image upload token');

		const signatureResponse = await fetch('https://api.yotpo.com/s3_signature', {
			method: 'POST',
			body: new URLSearchParams({ policy_name: 'ReviewImages', app_key: YOTPO_APP_KEY }),
		});
		if (!signatureResponse.ok) throw new Error(`Yotpo signature responded ${signatureResponse.status}`);
		const policy = await signatureResponse.json();
		const bucketUrl = `//${policy.bucket}.s3.amazonaws.com`;

		const results = await Promise.allSettled(
			images.map(async (image) => {
				const key = `${policy.path}${uploadToken}/${image.lastModified}_${image.name.replace(/[^\w.-]/g, '_')}`;
				const body = new FormData();
				body.append('key', key);
				body.append('acl', 'public-read');
				body.append('Content-Type', image.type);
				body.append('policy', policy.encoded_policy);
				body.append('x-amz-credential', policy.credential);
				body.append('x-amz-algorithm', 'AWS4-HMAC-SHA256');
				body.append('x-amz-date', policy.date);
				body.append('x-amz-meta-uuid', '14365123651274');
				body.append('x-amz-signature', policy.signature);
				body.append('file', image);

				const upload = await fetch(`https:${bucketUrl}`, { method: 'POST', body });
				if (!upload.ok) throw new Error(`S3 responded ${upload.status}`);

				const attach = await fetch('https://api-cdn.yotpo.com/images/process', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ image_upload_token: uploadToken, image_urls: [`${bucketUrl}/${key}`] }),
				});
				if (!attach.ok) throw new Error(`Yotpo image process responded ${attach.status}`);
			})
		);
		const failed = results.filter((result) => result.status === 'rejected');
		if (failed.length) throw new AggregateError(failed.map((result) => result.reason), `${failed.length} photo(s) failed`);
	}

	/** Chosen photos live in #reviewFiles rather than the input, so they can be added in batches and removed one by one. */
	#addReviewImages(input) {
		const files = [...this.#reviewFiles, ...input.files];
		const status = /** @type {HTMLElement} */ (input.form.querySelector('.product-links-reviews__form-status'));
		input.value = '';
		status.textContent = this.#strings.tooManyImages;
		status.hidden = files.length <= MAX_REVIEW_IMAGES;
		this.#reviewFiles = files.slice(0, MAX_REVIEW_IMAGES);
		this.#renderReviewImages();
	}

	#removeReviewImage(index) {
		this.#reviewFiles.splice(index, 1);
		this.#renderReviewImages();
		/** @type {HTMLElement} */ (this.querySelector('[data-review-images]')).focus();
	}

	#renderReviewImages() {
		const grid = this.querySelector('[data-review-previews]');
		const add = /** @type {HTMLElement} */ (grid.querySelector('[data-review-add]'));
		for (const preview of grid.querySelectorAll('[data-review-preview]')) {
			URL.revokeObjectURL(preview.querySelector('img').src);
			preview.remove();
		}
		add.insertAdjacentHTML(
			'beforebegin',
			this.#reviewFiles
				.map(
					(file, index) => `<li data-review-preview>
						<img src="${URL.createObjectURL(file)}" alt="${this.#escapeHtml(file.name)}">
						<button type="button" class="product-links-reviews__remove" data-remove-photo="${index}" aria-label="${this.#escapeHtml(this.#strings.removePhoto)}">${ICONS.remove}</button>
					</li>`
				)
				.join('')
		);
		add.hidden = this.#reviewFiles.length >= MAX_REVIEW_IMAGES;
	}

	/** Yotpo's own "top mentions" for the product, the same list its widget shows. */
	async #fetchTopics(productId) {
		const response = await fetch(`https://api-cdn.yotpo.com/v1/topic/${YOTPO_APP_KEY}/topics.json`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ domain_key: productId }),
		});
		if (!response.ok) throw new Error(`Yotpo topics responded ${response.status}`);
		return ((await response.json()).top_topics?.top_mention_topics ?? []).map((topic) => String(topic.name));
	}

	/**
	 * Pairs each topic with the reviews that mention it, keeping only topics enough reviews share.
	 * ponytail: matches word stems in the review text ("light" finds "lights"); Yotpo's tagging also counts synonyms,
	 * so counts can run lower than Yotpo's. Use its topic-filtered reviews endpoint if exact counts matter.
	 */
	#matchTopics(names, reviews, searchText) {
		return names
			.filter((name) => !IGNORED_TOPICS.has(name.toLowerCase()))
			.map((name) => {
				const stems = name
					.toLowerCase()
					.split(/\s+/)
					.map((word) => new RegExp(`\\b${word.replace(/s$/, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`));
				return { name, reviews: new Set(reviews.filter((review) => stems.every((stem) => stem.test(searchText.get(review))))) };
			})
			.filter((topic) => topic.reviews.size >= MIN_TOPIC_REVIEWS)
			.slice(0, MAX_TOPICS);
	}

	#topicChips(topics) {
		if (!topics.length) return '';
		const chips = topics
			.map(
				(topic, index) => `<button type="button" class="pills__pill" data-filter-topic="${index}" aria-pressed="false">
					${this.#escapeHtml(topic.name.replace(/^./, (c) => c.toUpperCase()))}
					<span class="product-links-reviews__topic-count">${topic.reviews.size}</span>
				</button>`
			)
			.join('');
		return `<section class="product-links-reviews__mentions" aria-labelledby="ProductLinksMentions">
			<h3 id="ProductLinksMentions" class="product-links-reviews__section-title">${this.#escapeHtml(this.#strings.mentionedIn)}</h3>
			<div class="product-links-reviews__topics">${chips}</div>
		</section>`;
	}

	async #fetchAllReviews(productId, sort) {
		const reviews = [];
		let bottomline;
		for (let page = 1; ; page++) {
			const url = `https://api-cdn.yotpo.com/v1/widget/${YOTPO_APP_KEY}/products/${productId}/reviews.json?per_page=${YOTPO_MAX_PER_PAGE}&page=${page}${sort}`;
			const response = await fetch(url);
			if (!response.ok) throw new Error(`Yotpo responded ${response.status}`);
			const data = (await response.json()).response;
			bottomline = data.bottomline;
			reviews.push(...data.reviews);
			if (!data.reviews.length || page * YOTPO_MAX_PER_PAGE >= data.pagination.total) return { bottomline, reviews };
		}
	}

	#reviewsSummary(bottomline) {
		const strings = this.#strings;
		const total = bottomline.total_review;
		const average = (Math.round(bottomline.average_score * 10) / 10).toFixed(1);
		const distribution = bottomline.star_distribution || {};
		const bars = [5, 4, 3, 2, 1]
			.map((star) => {
				const count = distribution[star] || 0;
				const label = this.#escapeHtml(`${strings.starsFilter.replace('[rating]', star)}: ${count}`);
				return `<li><button type="button" class="product-links-reviews__bar" data-filter-rating="${star}" aria-pressed="false" aria-label="${label}"${count ? '' : ' disabled'}>
					<span class="product-links-reviews__bar-star">${star}${ICONS.star}</span>
					<span class="product-links-reviews__bar-track"><span class="product-links-reviews__bar-fill" style="width: ${(count / total) * 100}%"></span></span>
					<span class="product-links-reviews__bar-count">${count}</span>
				</button></li>`;
			})
			.join('');
		const basedOn = total === 1 ? strings.basedOnOne : strings.basedOnOther.replace('[count]', total);

		return `<div class="product-links-reviews__summary">
			<div class="product-links-reviews__average">
				<span class="product-links-reviews__score">${average}</span>
				${this.#stars(Number(average))}
				<span class="product-links-reviews__total">${this.#escapeHtml(basedOn)}</span>
			</div>
			<ul class="product-links-reviews__bars" role="list">${bars}</ul>
		</div>`;
	}

	#trustBadge(logo) {
		const image = SAFE_URL.test(logo) ? `<img src="${logo}" alt="Yotpo" loading="lazy">` : '';
		return `<div class="product-links-reviews__trust">
			${ICONS.shield}
			<span><strong>${this.#escapeHtml(this.#strings.trustTitle)}</strong> ${this.#escapeHtml(this.#strings.trustText)}</span>
			${image}
		</div>`;
	}

	#photoStrip() {
		const photos = this.#photos;
		if (!photos.length) return '';
		const seeAll = this.#escapeHtml(this.#strings.seeAllPhotos.replace('[count]', photos.length));
		const tiles = photos
			.slice(0, STRIP_PHOTOS)
			.map(({ image }, index) => this.#photoButton(index, image.thumb_url))
			.join('');
		const more =
			photos.length > STRIP_PHOTOS
				? `<li><button type="button" class="product-links-reviews__photo product-links-reviews__photo--more" data-photo-index="${STRIP_PHOTOS}" aria-label="${seeAll}">
					<img src="${photos[STRIP_PHOTOS].image.thumb_url}" alt="" loading="lazy">
					<span class="product-links-reviews__photo-more-count" aria-hidden="true">+${photos.length - STRIP_PHOTOS}</span>
				</button></li>`
				: '';

		return `<section aria-labelledby="ProductLinksCustomerPhotos">
			<div class="product-links-reviews__section-head">
				<h3 id="ProductLinksCustomerPhotos" class="product-links-reviews__section-title">${this.#escapeHtml(this.#strings.customerPhotos)}</h3>
				<button type="button" class="product-links-reviews__link" data-photo-index="0">${seeAll}</button>
			</div>
			<ul class="product-links-reviews__strip" role="list">${tiles}${more}</ul>
		</section>`;
	}

	#photoButton(index, src) {
		const label = this.#strings.viewPhoto.replace('[index]', index + 1).replace('[count]', this.#photos.length);
		return `<li><button type="button" class="product-links-reviews__photo" data-photo-index="${index}" aria-label="${this.#escapeHtml(label)}">
			<img src="${src}" alt="" loading="lazy">
		</button></li>`;
	}

	#reviewItem(review) {
		const date = this.#formatDate(review.created_at);
		const name = this.#decode(review.user?.display_name || '');
		const verified = review.verified_buyer
			? `<span class="product-links-reviews__verified">${ICONS.verified}${this.#escapeHtml(this.#strings.verified)}</span>`
			: '';
		const images = this.#safeImages(review);

		return `<li class="product-links-reviews__item">
			<div class="product-links-reviews__byline">
				<p class="product-links-reviews__author">${this.#escapeHtml(name)} ${verified}</p>
				<p class="product-links-reviews__date"><time datetime="${this.#escapeHtml(review.created_at)}">${this.#escapeHtml(date)}</time></p>
			</div>
			${this.#stars(review.score)}
			${review.title ? `<h3 class="product-links-reviews__title">${this.#escapeYotpo(review.title)}</h3>` : ''}
			<p class="product-links-reviews__body" data-clamp>${this.#escapeYotpo(review.content)}</p>
			<button type="button" class="product-links-reviews__read-more" data-read-more hidden>${this.#escapeHtml(this.#strings.readMore)}</button>
			${
				images.length
					? `<ul class="product-links-reviews__images" role="list">${images
							.map((image) => this.#photoButton(this.#photoIndexOf.get(image), image.thumb_url))
							.join('')}</ul>`
					: ''
			}
			${review.comment?.content ? this.#reviewReply(review.comment) : ''}
		</li>`;
	}

	/** The store's public reply to a review, which Yotpo sends as the review's comment. */
	#reviewReply(comment) {
		const name = this.#decode(comment.display_name || '') || this.#strings.shopName;
		const avatar = SAFE_URL.test(comment.comments_avatar || '')
			? `<img src="${comment.comments_avatar}" alt="" width="20" height="20" loading="lazy">`
			: '';
		return `<div class="product-links-reviews__reply">
			<p class="product-links-reviews__reply-by">
				${avatar}
				<span><strong>${this.#escapeHtml(this.#strings.replyFrom.replace('[name]', name))}</strong> · ${this.#escapeHtml(this.#formatDate(comment.created_at))}</span>
			</p>
			<p class="product-links-reviews__body">${this.#escapeYotpo(comment.content)}</p>
		</div>`;
	}

	#formatDate(value) {
		return new Date(value).toLocaleDateString(document.documentElement.lang || undefined, {
			day: 'numeric',
			month: 'short',
			year: 'numeric',
		});
	}

	/** Only plain https URLs go into attributes, since #escapeHtml does not escape quotes. */
	#safeImages(review) {
		return (review.images_data || []).filter((image) =>
			[image.thumb_url, image.original_url].every((url) => SAFE_URL.test(url))
		);
	}

	/** Renders rounded-to-half stars using the review-stars icon styles from blocks/review.liquid. */
	#stars(rating) {
		const path = 'M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14l-5-4.87 6.91-1.01z';
		const rounded = Math.round(rating * 2) / 2;
		const icons = [1, 2, 3, 4, 5]
			.map((i) => {
				if (i <= rounded) return `<svg class="review-stars__icon review-stars__icon--full" viewBox="0 0 24 24" aria-hidden="true"><path d="${path}"/></svg>`;
				if (i - 0.5 === rounded) {
					return `<svg class="review-stars__icon review-stars__icon--half" viewBox="0 0 24 24" aria-hidden="true">
						<path class="review-stars__icon-empty-bg" d="${path}"/>
						<path d="${path}" style="clip-path: inset(0 50% 0 0)"/>
					</svg>`;
				}
				return `<svg class="review-stars__icon review-stars__icon--empty" viewBox="0 0 24 24" aria-hidden="true"><path d="${path}"/></svg>`;
			})
			.join('');
		const label = this.#escapeHtml(this.#strings.ratingOutOf5.replace('[rating]', rating));
		return `<span class="review-stars__icons" role="img" aria-label="${label}">${icons}</span>`;
	}

	/** Yotpo returns HTML-escaped text, so decode it before escaping to avoid double-encoded entities. */
	#escapeYotpo(str) {
		return this.#escapeHtml(this.#decode(str));
	}

	#decode(str) {
		const textarea = document.createElement('textarea');
		textarea.innerHTML = str;
		return textarea.value;
	}

	#bindVariantEvents() {
		const section = this.closest('.shopify-section, dialog');
		if (!section) return;

		section.addEventListener(ThemeEvents.variantUpdate, (event) => {
			const id = event.detail.resource?.id;
			if (!id) return;
			this.#selectedVariantId = String(id);
			const switcher = this.querySelector('.product-links-specs__switcher');
			if (switcher) /** @type {HTMLSelectElement} */ (switcher).value = this.#selectedVariantId;
			this.#renderVariantSpecsContent();
		});
	}

	#renderVariantSpecs() {
		const container = this.querySelector('[data-specs-content]');
		if (!container || !this.#variantSpecs?.length) return;

		let html = `<div class="product-links-specs__switcher-wrap">`;
		html += `<select class="product-links-specs__switcher">`;
		for (const v of this.#variantSpecs) {
			const selected = String(v.id) === String(this.#selectedVariantId) ? ' selected' : '';
			html += `<option value="${this.#escapeHtml(String(v.id))}"${selected}>${this.#escapeHtml(v.name)}</option>`;
		}
		html += `</select>`;
		html += `<svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path></svg>`;
		html += `</div>`;
		html += `<div class="product-links-specs__variant-content"></div>`;

		container.innerHTML = html;

		const switcher = container.querySelector('.product-links-specs__switcher');
		switcher.addEventListener('change', (e) => {
			this.#selectedVariantId = /** @type {HTMLSelectElement} */ (e.target).value;
			this.#renderVariantSpecsContent();
		});

		this.#renderVariantSpecsContent();
	}

	#renderVariantSpecsContent() {
		const contentEl = this.querySelector('.product-links-specs__variant-content');
		if (!contentEl || !this.#variantSpecs) return;

		const variant = this.#variantSpecs.find((v) => String(v.id) === String(this.#selectedVariantId));
		if (!variant) return;

		const html = Object.entries(variant.specs)
			.map(([category, entries]) => {
				const rows = Object.entries(entries)
					.map(
						([label, value]) =>
							`<div class="product-links-specs__row">
								<span class="product-links-specs__label">${this.#escapeHtml(label)}</span>
								<span class="product-links-specs__value">${this.#escapeHtml(String(value))}</span>
							</div>`
					)
					.join('');

				return `<div class="product-links-specs__category">
					<h3 class="product-links-specs__category-title">${this.#escapeHtml(this.#capitalize(category))}</h3>
					${rows}
				</div>`;
			})
			.join('');

		contentEl.innerHTML = html;
	}

	#renderSpecsTable() {
		const json = this.getAttribute('data-specifications');
		if (!json) return;

		const container = this.querySelector('[data-specs-content]');
		if (!container) return;

		try {
			const data = JSON.parse(json);
			const html = Object.entries(data)
				.map(([category, entries]) => {
					const rows = Object.entries(entries)
						.map(
							([label, value]) =>
								`<div class="product-links-specs__row">
                  <span class="product-links-specs__label">${this.#escapeHtml(label)}</span>
                  <span class="product-links-specs__value">${this.#escapeHtml(String(value))}</span>
                </div>`
						)
						.join('');

					return `<div class="product-links-specs__category">
            <h3 class="product-links-specs__category-title">${this.#escapeHtml(
							this.#capitalize(category)
						)}</h3>
            ${rows}
          </div>`;
				})
				.join('');

			container.innerHTML = html;
		} catch (e) {
			console.error('Failed to parse specifications JSON:', e);
		}
	}

	#capitalize(str) {
		return str.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
	}

	#escapeHtml(str) {
		const div = document.createElement('div');
		div.textContent = str;
		return div.innerHTML;
	}
}

if (!customElements.get('product-links-component')) {
	customElements.define('product-links-component', ProductLinksComponent);
}
